import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, MessagesSquare, Coins, Power, PowerOff, X, Eye } from "lucide-react";

const QTYPES = [
  { v: "choice", l: "Choix multiple" },
  { v: "rating", l: "Note 1-5" },
  { v: "text", l: "Texte libre" },
];

const CATS = [
  { v: "", l: "— aucune —" },
  { v: "music", l: "🎵 Musique" },
  { v: "humor", l: "😂 Humour" },
  { v: "cinema", l: "🎬 Cinéma" },
  { v: "dance", l: "💃 Danse" },
  { v: "art", l: "🎨 Art" },
  { v: "fashion", l: "👗 Mode" },
  { v: "food", l: "🍲 Cuisine" },
  { v: "general", l: "🌍 Général" },
];

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60";
const lbl = "text-[11px] uppercase tracking-widest text-white/50 font-bold block mb-1";

export const AdminSurveysPanel = () => {
  const [surveys, setSurveys] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);

  const empty = {
    title: "", description: "", category: "", reward_vola: 5,
    active: true, start_date: "", end_date: "",
    questions: [{ question_text: "", question_type: "choice", options: [""] }],
  };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/surveys").then(({ data }) => setSurveys(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updQ = (i, patch) => setForm((f) => ({
    ...f, questions: f.questions.map((q, j) => j === i ? { ...q, ...patch } : q),
  }));
  const updOpt = (qi, oi, v) => setForm((f) => ({
    ...f, questions: f.questions.map((q, j) => j === qi ? { ...q, options: q.options.map((o, k) => k === oi ? v : o) } : q),
  }));
  const addOpt = (qi) => setForm((f) => ({
    ...f, questions: f.questions.map((q, j) => j === qi ? { ...q, options: [...(q.options || []), ""] } : q),
  }));
  const removeOpt = (qi, oi) => setForm((f) => ({
    ...f, questions: f.questions.map((q, j) => j === qi ? { ...q, options: q.options.filter((_, k) => k !== oi) } : q),
  }));
  const addQuestion = () => setForm((f) => ({
    ...f, questions: [...f.questions, { question_text: "", question_type: "choice", options: [""] }],
  }));
  const removeQuestion = (i) => setForm((f) => ({
    ...f, questions: f.questions.filter((_, j) => j !== i),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Titre requis"); return; }
    if (!form.questions.length) { toast.error("Au moins une question requise"); return; }
    for (const q of form.questions) {
      if (!q.question_text) { toast.error("Toutes les questions doivent avoir un texte"); return; }
      if (q.question_type === "choice" && (!q.options?.length || q.options.some((o) => !o))) {
        toast.error("Chaque question 'Choix multiple' doit avoir des options non vides"); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        questions: form.questions.map((q) => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.question_type === "choice" ? q.options : null,
        })),
      };
      await api.post("/admin/surveys", payload);
      toast.success("Sondage créé ✨");
      setForm(empty); setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur création"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/surveys/${s.survey_id}`, { active: !s.active });
      setSurveys((prev) => prev.map((x) => x.survey_id === s.survey_id ? { ...x, active: s.active ? 0 : 1 } : x));
    } catch { toast.error("Erreur"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer le sondage "${s.title}" et toutes ses réponses ?`)) return;
    try {
      await api.delete(`/admin/surveys/${s.survey_id}`);
      setSurveys((prev) => prev.filter((x) => x.survey_id !== s.survey_id));
      toast.success("Supprimé");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-surveys-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-black">Sondages</h2>
          <p className="text-xs text-white/50">Crée des sondages sponsorisés. Chaque réponse crédite l'utilisateur en Vola.</p>
        </div>
        <button onClick={() => setCreating(!creating)} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="admin-survey-new-btn">
          {creating ? <><X size={14} /> Annuler</> : <><Plus size={14} /> Nouveau sondage</>}
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4" data-testid="admin-survey-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Titre *</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} required data-testid="survey-title" />
            </div>
            <div>
              <label className={lbl}>Catégorie</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp} data-testid="survey-category">
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={lbl}>Description</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp} data-testid="survey-desc" />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className={lbl}>Récompense Vola (par réponse)</label>
              <input type="number" min="0" value={form.reward_vola} onChange={(e) => setForm({ ...form, reward_vola: e.target.value })} className={inp} data-testid="survey-reward" />
            </div>
            <div>
              <label className={lbl}>Début (optionnel)</label>
              <input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inp} data-testid="survey-start" />
            </div>
            <div>
              <label className={lbl}>Fin (optionnel)</label>
              <input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inp} data-testid="survey-end" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-brand-red" data-testid="survey-active" />
            Activer immédiatement
          </label>

          <div className="border-t border-white/10 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Questions</p>
              <button type="button" onClick={addQuestion} className="text-xs text-brand-orange hover:underline inline-flex items-center gap-1" data-testid="add-question-btn">
                <Plus size={12} /> Ajouter une question
              </button>
            </div>
            {form.questions.map((q, qi) => (
              <div key={qi} className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2" data-testid={`question-${qi}`}>
                <div className="flex items-start gap-2">
                  <span className="shrink-0 w-7 h-7 rounded-lg bg-brand-orange/20 text-brand-orange flex items-center justify-center text-xs font-bold">{qi + 1}</span>
                  <input value={q.question_text} onChange={(e) => updQ(qi, { question_text: e.target.value })} placeholder="Texte de la question" className={inp} data-testid={`q-text-${qi}`} />
                  <select value={q.question_type} onChange={(e) => updQ(qi, { question_type: e.target.value, options: e.target.value === "choice" ? (q.options?.length ? q.options : [""]) : null })} className={inp + " !w-44"} data-testid={`q-type-${qi}`}>
                    {QTYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                  <button type="button" onClick={() => removeQuestion(qi)} className="text-white/40 hover:text-brand-red p-1.5" data-testid={`q-remove-${qi}`}><Trash2 size={14} /></button>
                </div>
                {q.question_type === "choice" && (
                  <div className="ml-9 space-y-1.5">
                    {(q.options || []).map((o, oi) => (
                      <div key={oi} className="flex gap-2">
                        <input value={o} onChange={(e) => updOpt(qi, oi, e.target.value)} placeholder={`Option ${oi + 1}`} className={inp} data-testid={`q-${qi}-opt-${oi}`} />
                        {q.options.length > 1 && <button type="button" onClick={() => removeOpt(qi, oi)} className="text-white/40 hover:text-brand-red p-1.5"><X size={14} /></button>}
                      </div>
                    ))}
                    <button type="button" onClick={() => addOpt(qi)} className="text-[11px] text-brand-orange hover:underline" data-testid={`q-${qi}-add-opt`}>+ ajouter une option</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50" data-testid="survey-submit">
            {busy ? "Création…" : "Créer le sondage"}
          </button>
        </form>
      )}

      <div className="space-y-2">
        {surveys.length === 0 && <p className="text-white/50 text-sm">Aucun sondage encore. Crée le premier ci-dessus.</p>}
        {surveys.map((s) => (
          <div key={s.survey_id} className={`rounded-xl border p-4 flex items-start gap-3 ${s.active ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={`admin-survey-row-${s.survey_id}`}>
            <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center"><MessagesSquare size={16} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{s.title}</p>
              <p className="text-xs text-white/50 truncate">{s.description || "—"}</p>
              <div className="flex items-center gap-3 mt-1 text-[11px] flex-wrap">
                <span className="inline-flex items-center gap-1 text-brand-yellow font-bold"><Coins size={10} /> +{s.reward_vola}</span>
                <span className="text-white/40">{s.responses_count || 0} réponses</span>
                {s.category && <span className="px-1.5 py-0.5 rounded bg-white/10 uppercase text-[9px]">{s.category}</span>}
                <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${s.active ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/50"}`}>{s.active ? "Actif" : "Inactif"}</span>
                <span className="text-white/40">· {(s.questions || []).length} question(s)</span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setViewing(s)} className="text-white/50 hover:text-white p-2" data-testid={`survey-view-${s.survey_id}`} title="Voir détails"><Eye size={14} /></button>
              <button onClick={() => toggleActive(s)} className="text-white/50 hover:text-brand-yellow p-2" data-testid={`survey-toggle-${s.survey_id}`} title={s.active ? "Désactiver" : "Activer"}>
                {s.active ? <PowerOff size={14} /> : <Power size={14} />}
              </button>
              <button onClick={() => remove(s)} className="text-white/40 hover:text-brand-red p-2" data-testid={`survey-delete-${s.survey_id}`} title="Supprimer"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-[#15100D] rounded-2xl border border-white/10 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="survey-detail-modal">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display text-xl font-black">{viewing.title}</h3>
                <p className="text-xs text-white/50">{viewing.responses_count || 0} réponses · +{viewing.reward_vola} Vola/réponse</p>
              </div>
              <button onClick={() => setViewing(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>
            {viewing.description && <p className="text-sm text-white/70 mb-4">{viewing.description}</p>}
            <ol className="space-y-3 list-decimal pl-5">
              {(viewing.questions || []).map((q) => (
                <li key={q.question_id} className="text-sm">
                  <p className="font-semibold">{q.question_text}</p>
                  <p className="text-[11px] text-white/40 uppercase tracking-widest">{q.question_type}</p>
                  {q.question_type === "choice" && Array.isArray(q.options) && (
                    <ul className="list-disc pl-5 text-white/70 text-xs mt-1">
                      {q.options.map((o, i) => <li key={i}>{o}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSurveysPanel;
